import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import {
  ArrowLeft, ZoomIn, ZoomOut, Maximize2, Minimize2, Hand, MapPin, Square, Hexagon, Pencil, Minus, LassoSelect,
  RotateCcw, ChevronLeft, ChevronRight, Eye, EyeOff, Loader2, Check, Camera, Lock, LockOpen, Undo2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MARKER_TOOLS, STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PositionDrawer from "@/components/PositionDrawer";
import PageHelp from "@/components/PageHelp";
import { pullDelta } from "@/lib/deltaSync";
import { ReloadButton } from "@/components/OfflineReadyBadge";
import { prefetchPlan } from "@/lib/offlinePrefetch";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const ICONS = { Hand, MapPin, Square, Hexagon, Pencil, Minus, LassoSelect };
const MAX_ZOOM = 10;
const toolColor = (t) => MARKER_TOOLS.find((m) => m.value === t)?.color || "#EF4444";

export default function PlanViewer() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activePos, setActivePos] = useState(searchParams.get("position") || null);
  const [plan, setPlan] = useState(null);
  const [positions, setPositions] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState("__all");
  const [members, setMembers] = useState([]);
  const [tool, setTool] = useState("hand");
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [showMarkers, setShowMarkers] = useState(true);
  // Badge size is remembered per plan on this device (falls back to the last global value).
  const sizeKey = `markerSize:${planId}`;
  const readSize = () => parseFloat(localStorage.getItem(sizeKey)) || parseFloat(localStorage.getItem("markerSize")) || 1;
  const [markerSize, setMarkerSize] = useState(readSize);
  useEffect(() => { setMarkerSize(readSize()); }, [planId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [newBadgeScale, setNewBadgeScale] = useState(() => parseFloat(localStorage.getItem("newBadgeScale")) || 1);
  const [fullscreen, setFullscreen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [polyPoints, setPolyPoints] = useState([]);
  const [imgUrl, setImgUrl] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const viewportRef = useRef(null);
  const planRef = useRef(null);
  const canvasRef = useRef(null);
  const panState = useRef(null);
  const pinchState = useRef(null);
  const isPdf = plan && (plan.content_type || "").includes("pdf");

  const photoInputRef = useRef(null);
  const photoTarget = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Marker moving: per user/view only, always starts blocked.
  const [moveOn, setMoveOn] = useState(false);
  const [drag, setDrag] = useState(null); // { id, dx, dy }
  const dragRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]); // [{ id, number, marker }] previous markers, newest last
  const [overlapPick, setOverlapPick] = useState(null); // { ids, x, y } when overlapping markers were tapped

  // Tapping a marker that overlaps others offers a short list to pick the wanted one.
  const openMarker = (id) => {
    const els = [...document.querySelectorAll('[data-testid^="position-drag-"]')];
    const me = els.find((el) => el.dataset.testid === `position-drag-${id}`);
    if (!me) { setActivePos(id); return; }
    const r = me.getBoundingClientRect();
    const hits = els.filter((el) => {
      if (el === me) return false;
      const o = el.getBoundingClientRect();
      return o.left < r.right && o.right > r.left && o.top < r.bottom && o.bottom > r.top;
    }).map((el) => el.dataset.testid.replace("position-drag-", ""));
    if (!hits.length) { setActivePos(id); return; }
    setOverlapPick({ ids: [id, ...hits], x: Math.min(r.left, window.innerWidth - 240), y: r.bottom + 6 });
  };

  // "Alle Schilder" slider: reset individual badge sizes on this plan so every badge ends up the same size.
  const equalizeBadges = () => {
    const odd = positions.filter((p) => p.badge_scale && p.badge_scale !== 1 && !String(p.id).startsWith("op_"));
    if (!odd.length) return;
    setPositions((ps) => ps.map((p) => ({ ...p, badge_scale: 1 })));
    odd.forEach((p) => api.put(`/positions/${p.id}`, { badge_scale: 1 }).catch(() => {}));
  };

  const undoMove = async () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setUndoStack((s) => s.slice(0, -1));
    setPositions((ps) => ps.map((p) => (p.id === last.id ? { ...p, marker: last.marker } : p)));
    try {
      await api.put(`/positions/${last.id}`, { marker: last.marker });
      toast.success(`${last.number} zurückgesetzt`);
    } catch (_) {
      toast.error("Rückgängig konnte nicht gespeichert werden");
      loadPositions();
    }
  };

  const startDrag = (e, pos) => {
    if (!moveOn) return;
    e.stopPropagation();
    const rect = planRef.current.getBoundingClientRect();
    dragRef.current = { id: pos.id, x: e.clientX, y: e.clientY, w: rect.width, h: rect.height, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moveDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 5) return;
    d.moved = true;
    setDrag({ id: d.id, dx: (e.clientX - d.x) / d.w, dy: (e.clientY - d.y) / d.h });
  };
  const endDrag = async () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !d.moved || !drag) { setDrag(null); return; }
    const pos = positions.find((p) => p.id === d.id);
    const marker = shiftMarker(pos.marker, drag.dx, drag.dy);
    setUndoStack((s) => [...s.slice(-19), { id: pos.id, number: pos.number, marker: pos.marker }]);
    setPositions((ps) => ps.map((p) => (p.id === d.id ? { ...p, marker } : p)));
    setDrag(null);
    window.__suppressMarkerClick = Date.now();
    try {
      const { data } = await api.put(`/positions/${d.id}`, { marker });
      toast.success(data?.__pending ? "Neue Lage offline gespeichert" : `${pos.number} verschoben`);
    } catch (_) {
      toast.error("Verschieben konnte nicht gespeichert werden");
      loadPositions();
    }
  };

  const capturePhoto = (posId) => {
    photoTarget.current = posId;
    photoInputRef.current?.click();
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !photoTarget.current) return;
    const posId = photoTarget.current;
    setUploadingPhoto(true);
    const loc = await new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 5000, maximumAge: 60000 });
    });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("position_id", posId);
    fd.append("capture_type", "kamera");
    fd.append("timestamp", "true");
    if (loc) { fd.append("lat", loc.lat); fd.append("lng", loc.lng); }
    try {
      await api.post("/media", fd);
      toast.success("Foto zur Position hinzugefügt");
      await loadPositions();
    } catch (err) { console.error("plan photo upload failed", err); toast.error("Foto-Upload fehlgeschlagen"); } finally { setUploadingPhoto(false); photoTarget.current = null; }
  };

  const loadPositions = useCallback(() => api.get(`/plans/${planId}/positions`).then((r) => setPositions(r.data)), [planId]);

  useEffect(() => {
    if (!planId) return;
    pullDelta(planId).then(loadPositions);
    const onOnline = () => pullDelta(planId).then(loadPositions);
    const iv = setInterval(() => { if (navigator.onLine) pullDelta(planId).then(loadPositions); }, 30000);
    window.addEventListener("online", onOnline);
    return () => { clearInterval(iv); window.removeEventListener("online", onOnline); };
  }, [planId, loadPositions]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await api.get(`/plans/${planId}`);
      setPlan(data);
      await loadPositions();
      const blob = await api.get(`/plans/${planId}/file`, { responseType: "blob" });
      if ((data.content_type || "").includes("pdf")) {
        const buf = await blob.data.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        setPdfDoc(doc); setNumPages(doc.numPages); setPageNum(1);
      } else {
        setImgUrl(URL.createObjectURL(blob.data));
      }
      setLoading(false);
    })();
  }, [planId, loadPositions]);

  useEffect(() => {
    if (!plan?.project_id) return;
    api.get("/tasks/assignees", { params: { project_id: plan.project_id } }).then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, [plan?.project_id]);

  const visiblePositions = assigneeFilter === "__all" ? positions
    : assigneeFilter === "__none" ? positions.filter((p) => !(p.assignee_ids || []).length)
    : positions.filter((p) => (p.assignee_ids || []).includes(assigneeFilter));

  // Re-render PDF sharper when zoomed in (tiers keep re-renders rare; area cap protects mobile memory).
  const renderTier = [1, 2, 4, 7, 10].find((t) => scale <= t * 1.05) || MAX_ZOOM;
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    let task = null;
    const timer = setTimeout(async () => {
      const page = await pdfDoc.getPage(pageNum);
      const base = page.getViewport({ scale: 1 });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let s = Math.max(2, (900 * renderTier * dpr) / base.width);
      const maxSide = 16000, maxArea = 16e6;
      s = Math.min(s, maxSide / base.width, maxSide / base.height, Math.sqrt(maxArea / (base.width * base.height)));
      const viewport = page.getViewport({ scale: s });
      const off = document.createElement("canvas");
      off.width = Math.floor(viewport.width); off.height = Math.floor(viewport.height);
      task = page.render({ canvasContext: off.getContext("2d"), viewport });
      try { await task.promise; } catch { return; }
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = off.width; canvas.height = off.height;
      canvas.getContext("2d").drawImage(off, 0, 0);
      off.width = 0; off.height = 0;
    }, canvasRef.current?.width ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); if (task) task.cancel(); };
  }, [pdfDoc, pageNum, renderTier]);

  const viewRef = useRef({ scale: 1, tx: 0, ty: 0 });
  viewRef.current = { scale, tx, ty };
  const zoomAt = (factor, cx, cy) => {
    const { scale: s, tx: t0, ty: t1 } = viewRef.current;
    const ns = Math.min(MAX_ZOOM, Math.max(0.1, s * factor));
    const rect = viewportRef.current.getBoundingClientRect();
    const px = cx - rect.left, py = cy - rect.top;
    const next = { scale: ns, tx: px - (px - t0) * (ns / s), ty: py - (py - t1) * (ns / s) };
    viewRef.current = next;
    setScale(next.scale); setTx(next.tx); setTy(next.ty);
  };

  const onWheel = (e) => { e.preventDefault(); zoomAt(e.deltaY < 0 ? 1.12 : 0.89, e.clientX, e.clientY); };

  // Pinch-to-zoom (zwei Finger) für Tablet/Handy
  const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const touchMid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      panState.current = null;
      setDraft(null);
      pinchState.current = { dist: touchDist(e.touches), mid: touchMid(e.touches), scale, tx, ty };
    }
  };
  const onTouchMove = (e) => {
    if (pinchState.current && e.touches.length === 2) {
      e.preventDefault();
      const start = pinchState.current;
      const nd = touchDist(e.touches);
      const nm = touchMid(e.touches);
      const ns = Math.min(MAX_ZOOM, Math.max(0.1, start.scale * (nd / start.dist)));
      const rect = viewportRef.current.getBoundingClientRect();
      const px = start.mid.x - rect.left, py = start.mid.y - rect.top;
      const f = ns / start.scale;
      setScale(ns);
      setTx(px - (px - start.tx) * f + (nm.x - start.mid.x));
      setTy(py - (py - start.ty) * f + (nm.y - start.mid.y));
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchState.current = null;
  };

  const getNorm = (e) => {
    const rect = planRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const createPosition = async (marker) => {
    const { data } = await api.post("/positions", { plan_id: planId, marker, title: "", badge_scale: newBadgeScale });
    toast.success(data.__pending ? "Position offline gespeichert – wird bei Verbindung übertragen" : `${data.number} erstellt`);
    await loadPositions();
    setActivePos(data.id);
    setTool("hand");
  };

  const onPointerDown = (e) => {
    if (pinchState.current) return;
    if (e.target.closest && e.target.closest('[data-testid^="position-marker-"], [data-testid^="position-photo-"], [data-testid^="position-drag-"]')) return;
    if (tool === "hand") {
      panState.current = { x: e.clientX, y: e.clientY, tx, ty };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (tool === "polygon") return; // handled on click
    const p = getNorm(e);
    if (tool === "point") { createPosition({ type: "point", x: p.x, y: p.y }); return; }
    setDraft({ type: tool, start: p, points: [p], ...p });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (pinchState.current) return;
    if (panState.current) {
      setTx(panState.current.tx + (e.clientX - panState.current.x));
      setTy(panState.current.ty + (e.clientY - panState.current.y));
      return;
    }
    if (!draft) return;
    const p = getNorm(e);
    if (tool === "rectangle" || tool === "area") {
      setDraft((d) => ({ ...d, box: { x: Math.min(d.start.x, p.x), y: Math.min(d.start.y, p.y), w: Math.abs(p.x - d.start.x), h: Math.abs(p.y - d.start.y) } }));
    } else if (tool === "line") {
      setDraft((d) => ({ ...d, end: p }));
    } else if (tool === "freehand") {
      setDraft((d) => ({ ...d, points: [...d.points, p] }));
    }
  };

  const onPointerUp = () => {
    if (panState.current) { panState.current = null; return; }
    if (!draft) return;
    const d = draft;
    setDraft(null);
    if ((d.type === "rectangle" || d.type === "area") && d.box && d.box.w > 0.005) {
      createPosition({ type: d.type, ...d.box });
    } else if (d.type === "line" && d.end) {
      createPosition({ type: "line", points: [d.start, d.end] });
    } else if (d.type === "freehand" && d.points.length > 2) {
      createPosition({ type: "freehand", points: d.points });
    }
  };

  const onPlanClick = (e) => {
    if (tool !== "polygon") return;
    setPolyPoints((pts) => [...pts, getNorm(e)]);
  };
  const finishPolygon = () => {
    if (polyPoints.length >= 3) createPosition({ type: "polygon", points: polyPoints });
    setPolyPoints([]);
  };

  const resetView = () => { setScale(1); setTx(0); setTy(0); };
  const toggleFs = () => setFullscreen((f) => !f);

  const drawColor = toolColor(tool);

  return (
    <div className={fullscreen ? "fixed inset-0 z-50 bg-slate-100 flex flex-col" : "space-y-4"}>
      {!fullscreen && (
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary" data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Zurück</button>
      )}

      <div className={`flex items-center justify-between gap-3 flex-wrap ${fullscreen ? "p-3 bg-white border-b" : ""}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0"><h1 className="text-xl font-head font-bold text-slate-900 truncate">{plan?.name || "Bauplan"}</h1><PageHelp id="bauplan" title="Bauplan & Marker" text="Setzen Sie Marker bzw. Positionen an die richtige Stelle im Plan. Über einen Marker erfassen Sie Tickets, Mängel, Fotos und Mengen. Die Schildgröße lässt sich anpassen." /></div>
          <p className="text-xs text-slate-500">{positions.length} markierte Positionen</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <ReloadButton testid="plan-offline-reload" load={() => prefetchPlan(planId)} onDone={() => loadPositions()}
            okMsg="Bauplan ist jetzt offline aktuell" title="Plan, Positionen und Fotos erneut für offline laden" />
          <Button variant={moveOn ? "default" : "outline"} size="sm" onClick={() => setMoveOn((v) => !v)} data-testid="marker-move-toggle"
            title={moveOn ? "Marker verschieben: zugelassen – tippen zum Blockieren" : "Marker verschieben: blockiert – tippen zum Zulassen"}>
            {moveOn ? <LockOpen className="h-4 w-4 mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
            <span className="text-xs">{moveOn ? "Verschieben zugelassen" : "Verschieben blockiert"}</span>
          </Button>
          {undoStack.length > 0 && (
            <Button variant="outline" size="sm" onClick={undoMove} data-testid="marker-move-undo"
              title={`Letztes Verschieben rückgängig machen (${undoStack[undoStack.length - 1].number})`}>
              <Undo2 className="h-4 w-4 mr-1" /><span className="text-xs">Rückgängig ({undoStack.length})</span>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowMarkers((s) => !s)} data-testid="toggle-markers-btn">{showMarkers ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</Button>
          <Button variant="outline" size="sm" onClick={() => zoomAt(0.83, window.innerWidth / 2, window.innerHeight / 2)} data-testid="zoom-out-btn"><ZoomOut className="h-4 w-4" /></Button>
          <span className="text-xs font-mono-tech w-12 text-center text-slate-600">{Math.round(scale * 100)}%</span>
          <Button variant="outline" size="sm" onClick={() => zoomAt(1.2, window.innerWidth / 2, window.innerHeight / 2)} data-testid="zoom-in-btn"><ZoomIn className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={resetView} data-testid="reset-view-btn"><RotateCcw className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={toggleFs} data-testid="fullscreen-btn">{fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}</Button>
        </div>
      </div>

      {/* Tool ribbon */}
      <div className={`flex items-center gap-1.5 flex-wrap ${fullscreen ? "px-3 pb-2 bg-white" : "bg-white border border-slate-200 rounded-xl p-2 shadow-sm"}`}>
        <span className="text-xs text-slate-400 px-2 hidden sm:inline">Werkzeug:</span>
        {[{ value: "hand", label: "Verschieben", color: "#64748B", icon: "Hand" }, ...MARKER_TOOLS].map((t) => {
          const Icon = ICONS[t.icon];
          const active = tool === t.value;
          return (
            <button key={t.value} onClick={() => { setTool(t.value); setPolyPoints([]); setDraft(null); }}
              data-testid={`marker-${t.value}-tool`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${active ? "text-white shadow" : "text-slate-600 hover:bg-slate-100"}`}
              style={active ? { background: t.color } : {}}>
              <Icon className="h-3.5 w-3.5" />{t.label}
            </button>
          );
        })}
        {tool === "polygon" && polyPoints.length >= 3 && (
          <Button size="sm" onClick={finishPolygon} className="ml-2 h-8" data-testid="finish-polygon-btn"><Check className="h-3.5 w-3.5 mr-1" />Polygon abschließen ({polyPoints.length})</Button>
        )}
        <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200" data-testid="marker-size-control">
          <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span className="text-xs text-slate-400 hidden sm:inline">Alle Schilder</span>
          <input type="range" min="0.3" max="2" step="0.1" value={markerSize}
            onChange={(e) => { const v = parseFloat(e.target.value); setMarkerSize(v); localStorage.setItem(sizeKey, String(v)); equalizeBadges(); }}
            className="w-20 sm:w-24 accent-primary cursor-pointer" data-testid="marker-size-slider" title="Größe aller Markerschilder auf diesem Plan (wird pro Plan gemerkt) – danach sind alle Schilder gleich groß" />
          <span className="text-xs font-mono-tech text-slate-500 w-9 text-right">{Math.round(markerSize * 100)}%</span>
        </div>
        <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200" data-testid="new-badge-scale-control">
          <span className="text-xs text-slate-400">Neue Position</span>
          <input type="range" min="0.3" max="1.2" step="0.05" value={newBadgeScale}
            onChange={(e) => { const v = parseFloat(e.target.value); setNewBadgeScale(v); localStorage.setItem("newBadgeScale", String(v)); }}
            className="w-20 sm:w-24 accent-primary cursor-pointer" data-testid="new-badge-scale-slider" title="Schildgröße für neu erstellte Positionen" />
          <span className="text-xs font-mono-tech text-slate-500 w-9 text-right" data-testid="new-badge-scale-value">{Math.round(newBadgeScale * 100)}%</span>
        </div>
        <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200" data-testid="assignee-filter-control">
          <span className="text-xs text-slate-400">Zuständig</span>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="plan-assignee-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Alle Positionen</SelectItem>
              <SelectItem value="__none">Ohne Zuständigen</SelectItem>
              {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {assigneeFilter !== "__all" && <span className="text-xs text-slate-500" data-testid="plan-assignee-filter-count">{visiblePositions.length}/{positions.length}</span>}
        </div>
        {isPdf && (
          <div className="flex items-center gap-1 ml-auto">
            <Button variant="outline" size="sm" disabled={pageNum <= 1} onClick={() => setPageNum((p) => p - 1)} data-testid="pdf-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-xs font-mono-tech">Seite {pageNum}/{numPages}</span>
            <Button variant="outline" size="sm" disabled={pageNum >= numPages} onClick={() => setPageNum((p) => p + 1)} data-testid="pdf-next-page"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      {/* Viewport */}
      <div ref={viewportRef} onWheel={onWheel}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        className={`plan-canvas-wrap relative overflow-hidden bg-slate-200 border border-slate-300 ${fullscreen ? "flex-1" : "rounded-xl"}`}
        style={{ height: fullscreen ? "auto" : "70vh", cursor: tool === "hand" ? "grab" : "crosshair", touchAction: "none" }}
        data-testid="plan-viewport">
        {loading && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}
        <div style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: "0 0", padding: 40 }}>
          <div ref={planRef} className="relative inline-block bg-white shadow-xl"
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onClick={onPlanClick}>
            {isPdf ? (
              <canvas ref={canvasRef} className="block max-w-none" style={{ width: 900 }} />
            ) : imgUrl ? (
              <img src={imgUrl} alt={plan?.name} className="block max-w-none select-none" style={{ width: 900 }} draggable={false} />
            ) : null}

            {/* SVG shape overlay */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1 1" preserveAspectRatio="none">
              {showMarkers && visiblePositions.map((pos) => <ShapeMarker key={pos.id} pos={drag?.id === pos.id ? { ...pos, marker: shiftMarker(pos.marker, drag.dx, drag.dy) } : pos} />)}
              {draft && draft.type === "rectangle" && draft.box && <rect x={draft.box.x} y={draft.box.y} width={draft.box.w} height={draft.box.h} fill="none" stroke={drawColor} strokeWidth="0.004" strokeDasharray="0.01" vectorEffect="non-scaling-stroke" />}
              {draft && draft.type === "area" && draft.box && <rect x={draft.box.x} y={draft.box.y} width={draft.box.w} height={draft.box.h} fill={drawColor} fillOpacity="0.25" stroke={drawColor} strokeWidth="0.004" vectorEffect="non-scaling-stroke" />}
              {draft && draft.type === "line" && draft.end && <line x1={draft.start.x} y1={draft.start.y} x2={draft.end.x} y2={draft.end.y} stroke={drawColor} strokeWidth="0.006" vectorEffect="non-scaling-stroke" />}
              {draft && draft.type === "freehand" && <polyline points={draft.points.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke={drawColor} strokeWidth="0.006" vectorEffect="non-scaling-stroke" />}
              {polyPoints.length > 0 && <polyline points={polyPoints.map((p) => `${p.x},${p.y}`).join(" ")} fill={toolColor("polygon")} fillOpacity="0.15" stroke={toolColor("polygon")} strokeWidth="0.005" vectorEffect="non-scaling-stroke" />}
            </svg>

            {/* HTML pin badges */}
            {showMarkers && visiblePositions.map((pos) => (
              <PinBadge key={pos.id} pos={drag?.id === pos.id ? { ...pos, marker: shiftMarker(pos.marker, drag.dx, drag.dy) } : pos}
                size={markerSize * (pos.badge_scale || 1)} movable={moveOn} dragging={drag?.id === pos.id}
                onDragStart={(e) => startDrag(e, pos)} onDragMove={moveDrag} onDragEnd={endDrag}
                onClick={() => openMarker(pos.id)} onPhoto={() => capturePhoto(pos.id)} photoDisabled={uploadingPhoto} />
            ))}
            {polyPoints.map((p, i) => (
              <span key={`${p.x}-${p.y}-${i}`} className="absolute h-2 w-2 rounded-full bg-violet-600 -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }} />
            ))}
          </div>
        </div>
      </div>

      {overlapPick && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOverlapPick(null)} data-testid="overlap-pick-backdrop" />
          <div className="fixed z-[56] w-56 rounded-xl border border-slate-200 bg-white shadow-xl p-1.5 fade-up"
            style={{ left: Math.max(8, overlapPick.x), top: Math.min(overlapPick.y, window.innerHeight - 60 - overlapPick.ids.length * 44) }}
            data-testid="overlap-pick-menu">
            <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Mehrere Positionen hier</div>
            {overlapPick.ids.map((id) => {
              const p = positions.find((x) => x.id === id);
              if (!p) return null;
              const st = STATUS[p.status] || STATUS.offen;
              return (
                <button key={id} type="button" onClick={() => { setOverlapPick(null); setActivePos(id); }} data-testid={`overlap-pick-${id}`}
                  className="w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-slate-100 transition-colors">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: st.dot }} />
                  <span className="font-mono-tech text-xs font-bold">{p.number}</span>
                  <span className="text-xs text-slate-500 truncate">{p.title || p.room || ""}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      {activePos && <PositionDrawer positionId={activePos} initialTab={searchParams.get("tab")} onClose={() => setActivePos(null)} onChanged={loadPositions} />}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhotoSelected} data-testid="plan-photo-input" />
      {uploadingPhoto && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-slate-900 text-white text-sm px-4 py-2.5 rounded-full shadow-xl flex items-center gap-2" data-testid="plan-photo-uploading">
          <Loader2 className="h-4 w-4 animate-spin" />Foto wird hochgeladen…
        </div>
      )}
    </div>
  );
}

const clamp01 = (v) => Math.min(1, Math.max(0, v));

// Move a marker by (dx, dy) in plan-relative units, keeping it inside the plan.
function shiftMarker(m, dx, dy) {
  if (!m) return m;
  if (m.type === "point") return { ...m, x: clamp01(m.x + dx), y: clamp01(m.y + dy) };
  if (m.type === "rectangle" || m.type === "area") {
    return { ...m, x: Math.min(1 - (m.w || 0), Math.max(0, m.x + dx)), y: Math.min(1 - (m.h || 0), Math.max(0, m.y + dy)) };
  }
  if (m.points) {
    const xs = m.points.map((p) => p.x), ys = m.points.map((p) => p.y);
    const ddx = Math.min(1 - Math.max(...xs), Math.max(-Math.min(...xs), dx));
    const ddy = Math.min(1 - Math.max(...ys), Math.max(-Math.min(...ys), dy));
    return { ...m, points: m.points.map((p) => ({ x: p.x + ddx, y: p.y + ddy })) };
  }
  return m;
}

function anchorOf(m) {
  if (!m) return { x: 0.5, y: 0.5 };
  if (m.type === "point") return { x: m.x, y: m.y };
  if (m.type === "rectangle" || m.type === "area") return { x: m.x + (m.w || 0) / 2, y: m.y + (m.h || 0) / 2 };
  const pts = m.points || [];
  if (pts.length) return { x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length };
  return { x: 0.5, y: 0.5 };
}

function ShapeMarker({ pos }) {
  const m = pos.marker || {};
  const color = toolColor(m.type);
  if (m.type === "rectangle") return <rect x={m.x} y={m.y} width={m.w} height={m.h} fill={color} fillOpacity="0.12" stroke={color} strokeWidth="0.005" vectorEffect="non-scaling-stroke" />;
  if (m.type === "area") return <rect x={m.x} y={m.y} width={m.w} height={m.h} fill={color} fillOpacity="0.3" stroke={color} strokeWidth="0.005" vectorEffect="non-scaling-stroke" />;
  if (m.type === "line" && m.points) return <line x1={m.points[0].x} y1={m.points[0].y} x2={m.points[1].x} y2={m.points[1].y} stroke={color} strokeWidth="0.007" vectorEffect="non-scaling-stroke" />;
  if (m.type === "polygon" && m.points) return <polygon points={m.points.map((p) => `${p.x},${p.y}`).join(" ")} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="0.005" vectorEffect="non-scaling-stroke" />;
  if (m.type === "freehand" && m.points) return <polyline points={m.points.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke={color} strokeWidth="0.006" vectorEffect="non-scaling-stroke" />;
  return null;
}

function PinBadge({ pos, onClick, onPhoto, photoDisabled, size = 1, movable, dragging, onDragStart, onDragMove, onDragEnd }) {
  const a = anchorOf(pos.marker);
  const st = STATUS[pos.status] || STATUS.offen;
  const open = () => { if (Date.now() - (window.__suppressMarkerClick || 0) > 400) onClick(); };
  return (
    <div className={`absolute -translate-x-1/2 -translate-y-full group hover:z-30 ${dragging ? "z-20 opacity-90" : "pin-drop"} ${movable ? "cursor-move" : ""}`}
      style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, touchAction: movable ? "none" : undefined }}
      onPointerDown={movable ? onDragStart : undefined} onPointerMove={movable ? onDragMove : undefined}
      onPointerUp={movable ? onDragEnd : undefined} onPointerCancel={movable ? onDragEnd : undefined}
      data-testid={`position-drag-${pos.id}`}>
      <div className="flex flex-col items-center" style={{ transform: `scale(${size})`, transformOrigin: "bottom center" }}>
        <div className={`rounded-lg bg-white shadow-lg border-2 px-1.5 py-1 flex items-center gap-1.5 whitespace-nowrap ${movable ? "ring-2 ring-primary/40 ring-offset-1" : ""} ${dragging ? "shadow-2xl" : ""}`} style={{ borderColor: st.dot }}>
          <button onClick={(e) => { e.stopPropagation(); open(); }} data-testid={`position-marker-${pos.id}`}
            className="flex items-center gap-1.5 hover:scale-105 transition-transform">
            <span className="h-2 w-2 rounded-full" style={{ background: st.dot }} />
            <span className="font-mono-tech text-[10px] font-bold text-slate-900">{pos.number}</span>
            {pos.ticket_count > 0 && <Badge variant="outline" className="h-4 px-1 text-[9px] bg-sky-50 text-sky-700 border-sky-200">T{pos.ticket_count}</Badge>}
            {pos.defect_count > 0 && <Badge variant="outline" className="h-4 px-1 text-[9px] bg-red-50 text-red-700 border-red-200">M{pos.defect_count}</Badge>}
            {pos.photo_count > 0 && <Badge variant="outline" className="h-4 px-1 text-[9px] bg-slate-50 text-slate-700 border-slate-200">📷{pos.photo_count}</Badge>}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onPhoto(); }} disabled={photoDisabled} data-testid={`position-photo-${pos.id}`} title="Foto aufnehmen"
            className="ml-0.5 h-6 w-6 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <Camera className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="h-2.5 w-2.5 rotate-45 -mt-1 border-b-2 border-r-2 bg-white" style={{ borderColor: st.dot }} />
      </div>
    </div>
  );
}
